/* Two-level approval of a report period: Finance first, then the Account Manager (AM). Pure functions over the
 * report_approval rows (see db/schema.sql), so the same rules carry over to a real database unchanged. */
import { managerOf } from './constants.js';

export const FINANCE = 'finance';
export const AM = 'account_manager';
export const LEVEL_NAME = { [FINANCE]: 'Finance', [AM]: 'Account Manager' };

const sameReport = (a, r) => a.settlement_report_id === r.settlement_report_id && a.report_year === r.report_year && a.as_at_date === r.as_at_date;
const samePeriod = (a, p) => a.period_type === p.type && a.period_start === p.start;

/** { finance: row|null, account_manager: row|null, stage: 'none' | 'finance' | 'complete' } for one report period. */
export function statusOf(rows, report, period) {
  const mine = rows.filter((a) => sameReport(a, report) && samePeriod(a, period));
  const finance = mine.find((a) => a.approval_level === FINANCE) || null;
  const am = mine.find((a) => a.approval_level === AM) || null;
  return { finance, account_manager: am, stage: finance && am ? 'complete' : finance ? 'finance' : 'none' };
}

/** Can this user give this level of approval now? -> { ok, reason } (reason is shown to the user when not ok). */
export function canApprove(user, level, report, status) {
  if (status[level]) return { ok: false, reason: 'Already approved.' };
  if (!user) return { ok: false, reason: 'Your account is not set up as an approver.' };
  if (level === FINANCE) {
    return user.role === FINANCE ? { ok: true } : { ok: false, reason: 'Only Finance can give Finance approval.' };
  }
  if (!status.finance) return { ok: false, reason: 'Waiting for Finance approval first.' };
  const owner = managerOf(report);
  if (user.role !== AM) return { ok: false, reason: 'Only the Account Manager can give AM approval.' };
  if (String(user.account_manager || '').toLowerCase() !== owner.toLowerCase()) return { ok: false, reason: `Only ${owner}, the account manager for this report, can give AM approval.` };
  return { ok: true };
}

/** Appends an approval to the db document (mutates `db`) and returns the new row. Throws if the rules are not met. */
export function addApproval(db, { level, report, period, user, now = new Date() }) {
  const status = statusOf(db.report_approval, report, period);
  const check = canApprove(user, level, report, status);
  if (!check.ok) throw new Error(check.reason);
  const next = db.report_approval.reduce((m, a) => Math.max(m, Number(String(a.approval_id).replace(/\D/g, '')) || 0), 0) + 1;
  const row = {
    approval_id: `apr-${String(next).padStart(4, '0')}`,
    settlement_report_id: report.settlement_report_id, report_year: report.report_year, as_at_date: report.as_at_date,
    period_type: period.type, period_label: period.label, period_start: period.start,
    approval_level: level, approved_by_user_id: user.user_id, approved_by_name: user.display_name, approved_at: now.toISOString(),
  };
  db.report_approval.push(row);
  return row;
}

export const fmtWhen = (iso) => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso || '') : new Intl.DateTimeFormat('en-GB', { dateStyle: 'short', timeStyle: 'short' }).format(d).replace(',', '');
};
export const describe = (row) => (row ? `${row.approved_by_name}, ${fmtWhen(row.approved_at)}` : null);
export const stageLabel = (stage) => (stage === 'complete' ? 'Approved' : stage === 'finance' ? 'Finance approved' : 'Not approved');
